import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import Layout from '@/components/Layout';
import {
  FileSearch, Upload, Loader2, Sparkles, AlertTriangle, CheckCircle2,
  Calendar, DollarSign, FileText, User, Building, Clock, RotateCcw, X
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const ConfidencePill = ({ value }) => {
  const v = value || 0;
  const palette = v >= 90 ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                : v >= 70 ? 'bg-amber-50 text-amber-700 border-amber-200'
                : v >  0  ? 'bg-orange-50 text-orange-700 border-orange-200'
                          : 'bg-gray-50 text-gray-500 border-gray-200';
  return <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded-md border ${palette}`}>{v}%</span>;
};

const Field = ({ label, valueObj, icon: Icon, suffix }) => {
  const v = valueObj?.value;
  const conf = valueObj?.confidence ?? 0;
  return (
    <div className="p-3 bg-white rounded-xl border border-gray-100 hover:border-gray-200 transition-colors">
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1">
          {Icon && <Icon className="h-3 w-3" />} {label}
        </div>
        <ConfidencePill value={conf} />
      </div>
      <div className="text-sm font-bold text-gray-900 truncate">
        {v != null && v !== '' ? `${v}${suffix ? ` ${suffix}` : ''}` : <span className="text-gray-300 font-normal">—</span>}
      </div>
    </div>
  );
};

export default function LeaseAbstract() {
  const { i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const [stage, setStage] = useState('upload'); // upload · processing · result · error
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const handleFile = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.pdf')) { setError(isAr ? 'فقط ملفات PDF' : 'PDF files only'); return; }
    if (f.size > 10 * 1024 * 1024) { setError(isAr ? 'الملف كبير' : 'File too large'); return; }
    setFile(f); setError('');
  };

  const submit = async () => {
    if (!file) return;
    setStage('processing'); setError('');
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await axios.post(`${API}/ai/lease-abstract`, fd, { headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' } });
      setResult(r.data);
      setStage('result');
    } catch (e) {
      setError(e?.response?.data?.detail || (isAr ? 'فشل التحليل' : 'Extraction failed'));
      setStage('error');
    }
  };

  const ext = result?.extraction || {};
  const obligations = ext.key_obligations || [];
  const keyDates = ext.key_dates || [];

  return (
    <Layout>
      <div className="space-y-6 max-w-5xl mx-auto">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
            <FileSearch className="h-5 w-5 text-purple-700" strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-bold text-gray-900">{isAr ? 'الاستخلاص الذكي للعقود' : 'AI Lease Abstraction'}</h1>
            <p className="text-sm text-gray-500">{isAr ? 'ارفع عقد إيجار PDF — يستخرج REVA البنود والمواعيد والمبالغ تلقائياً.' : 'Upload a lease PDF — REVA extracts clauses, dates, and amounts automatically.'}</p>
          </div>
        </div>

        {stage === 'upload' && (
          <Card className="shadow-soft border-gray-100">
            <CardContent className="pt-6 pb-6 space-y-5">
              <label className="block border-2 border-dashed border-gray-200 rounded-2xl p-12 text-center hover:border-primary/40 transition-colors cursor-pointer" data-testid="lease-drop">
                <input type="file" className="hidden" accept=".pdf" onChange={e => handleFile(e.target.files[0])} data-testid="lease-file-input" />
                <Upload className="h-10 w-10 text-gray-400 mx-auto mb-3" />
                {file ? (
                  <div>
                    <p className="font-semibold text-primary">{file.name}</p>
                    <p className="text-xs text-gray-500 mt-1">{(file.size / 1024).toFixed(1)} KB · PDF</p>
                  </div>
                ) : (
                  <div>
                    <p className="font-medium text-gray-900">{isAr ? 'اسحب وأفلت عقد PDF أو اضغط للاختيار' : 'Drop a lease PDF or click to choose'}</p>
                    <p className="text-xs text-gray-500 mt-1">{isAr ? 'PDF فقط · 10MB كحد أقصى' : '.pdf only · 10MB max'}</p>
                  </div>
                )}
              </label>

              <div className="p-3 bg-amber-50/60 border border-amber-200 rounded-xl text-xs text-amber-800 flex items-start gap-2">
                <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                <div>
                  <b>{isAr ? 'تنبيه:' : 'Disclaimer:'}</b> {isAr ? 'الناتج مسوّدة بمساعدة الذكاء الاصطناعي. راجع كل حقل قبل اعتماده. لا ترسل تلقائياً.' : 'Output is an AI-assisted draft. Review every field before finalising. Never auto-send.'}
                </div>
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700 flex items-start gap-2">
                  <X className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />{error}
                </div>
              )}

              <div className="flex justify-end">
                <Button onClick={submit} disabled={!file} className="gradient-primary text-white rounded-xl" data-testid="lease-extract-btn">
                  <Sparkles className="h-4 w-4 me-2" />
                  {isAr ? 'استخلص بالذكاء الاصطناعي' : 'Extract with AI'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {stage === 'processing' && (
          <Card className="shadow-soft border-gray-100">
            <CardContent className="py-16 flex flex-col items-center text-center gap-3">
              <Loader2 className="h-8 w-8 text-purple-600 animate-spin" />
              <div className="font-heading font-bold text-gray-900">{isAr ? 'يقرأ REVA العقد...' : 'REVA is reading the contract...'}</div>
              <div className="text-xs text-gray-500 max-w-sm">{isAr ? 'نُحلل النص ونستخرج البنود والمواعيد. عادة تستغرق 10–20 ثانية.' : 'Parsing the text and extracting clauses, dates, and obligations. Typically 10–20 seconds.'}</div>
            </CardContent>
          </Card>
        )}

        {stage === 'result' && result && (
          <div className="space-y-5">
            {/* Header */}
            <Card className="shadow-glass border-purple-100 bg-gradient-to-br from-purple-50/40 via-white to-primary/5">
              <CardContent className="pt-5 pb-5">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                  <h2 className="font-heading text-lg font-bold text-gray-900">{isAr ? 'تم الاستخلاص بنجاح' : 'Extraction complete'}</h2>
                </div>
                <div className="text-xs text-gray-500">{result.filename} · {result.characters_processed} {isAr ? 'حرف معالج' : 'chars processed'}</div>
                {ext.summary && <p className="mt-3 text-sm text-gray-700 leading-relaxed">{ext.summary}</p>}
              </CardContent>
            </Card>

            {/* Field grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Field label={isAr ? 'المستأجر' : 'Tenant'}            valueObj={ext.tenant_name}        icon={User} />
              <Field label={isAr ? 'المالك' : 'Landlord'}            valueObj={ext.landlord_name}      icon={User} />
              <Field label={isAr ? 'العنوان' : 'Address'}            valueObj={ext.property_address}   icon={Building} />
              <Field label={isAr ? 'الإيجار الشهري' : 'Monthly rent'} valueObj={ext.monthly_rent}       icon={DollarSign} suffix={ext.monthly_rent?.currency || 'SAR'} />
              <Field label={isAr ? 'الإيجار السنوي' : 'Annual rent'}  valueObj={ext.annual_rent}        icon={DollarSign} suffix={ext.annual_rent?.currency || 'SAR'} />
              <Field label={isAr ? 'التأمين' : 'Deposit'}             valueObj={ext.security_deposit}   icon={DollarSign} suffix={ext.security_deposit?.currency || 'SAR'} />
              <Field label={isAr ? 'تاريخ البدء' : 'Start date'}      valueObj={ext.start_date}         icon={Calendar} />
              <Field label={isAr ? 'تاريخ النهاية' : 'End date'}      valueObj={ext.end_date}           icon={Calendar} />
              <Field label={isAr ? 'تكرار الدفع' : 'Frequency'}      valueObj={ext.payment_frequency}  icon={Clock} />
              <Field label={isAr ? 'بنود التصاعد' : 'Escalation'}     valueObj={ext.escalation_terms}   icon={FileText} />
              <Field label={isAr ? 'مدة الإشعار' : 'Notice period'}    valueObj={ext.notice_period_days} icon={Clock} suffix={isAr ? 'يوم' : 'days'} />
              <Field label={isAr ? 'الوحدة' : 'Unit'}                 valueObj={ext.unit_identifier}    icon={Building} />
            </div>

            {/* Obligations */}
            {obligations.length > 0 && (
              <Card className="shadow-soft border-gray-100">
                <CardContent className="pt-5 pb-5">
                  <h3 className="font-heading text-sm font-bold text-gray-900 mb-3">{isAr ? 'الالتزامات الرئيسية' : 'Key obligations'}</h3>
                  <div className="space-y-2">
                    {obligations.map((o, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm text-gray-700">
                        <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                        <span>{o}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Key dates */}
            {keyDates.length > 0 && (
              <Card className="shadow-soft border-gray-100">
                <CardContent className="pt-5 pb-5">
                  <h3 className="font-heading text-sm font-bold text-gray-900 mb-3">{isAr ? 'التواريخ الرئيسية' : 'Key dates'}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {keyDates.map((d, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-gray-50/60 rounded-md text-sm">
                        <span className="text-gray-700">{d.label}</span>
                        <span className="font-mono font-bold text-primary">{d.date}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Disclaimer footer */}
            <div className="p-3 bg-amber-50/60 border border-amber-200 rounded-xl text-xs text-amber-800 flex items-start gap-2">
              <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
              {result.disclaimer || (isAr ? 'مسوّدة بمساعدة الذكاء الاصطناعي — راجع قبل اعتمادها.' : 'AI-assisted draft — review before finalising.')}
            </div>

            <div className="flex justify-between gap-2">
              <Button onClick={() => { setStage('upload'); setFile(null); setResult(null); }} variant="outline" className="rounded-xl" data-testid="lease-restart">
                <RotateCcw className="h-4 w-4 me-2" />{isAr ? 'استخلص عقد آخر' : 'Extract another lease'}
              </Button>
            </div>
          </div>
        )}

        {stage === 'error' && (
          <Card className="shadow-soft border-red-100">
            <CardContent className="py-12 text-center">
              <AlertTriangle className="h-10 w-10 text-red-500 mx-auto mb-3" />
              <h3 className="font-heading text-lg font-bold text-gray-900 mb-2">{isAr ? 'فشل الاستخلاص' : 'Extraction failed'}</h3>
              <p className="text-sm text-gray-500 max-w-md mx-auto">{error}</p>
              <Button onClick={() => setStage('upload')} variant="outline" className="rounded-xl mt-4">{isAr ? 'حاول مرة أخرى' : 'Try again'}</Button>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
